import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  Stack,
  LinearProgress,
  Pagination,
  PaginationItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ForumIcon from '@mui/icons-material/Forum';
import SchoolIcon from '@mui/icons-material/School';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { supabase } from '../services/supabase';
import { useApiHealth } from '../hooks/useApiHealth';
import { isApplePlatform, hasSendModifier } from '../utils/platform';

// ─── Design Tokens ─────────────────────────────────────────────────────────────
const ORANGE = '#E55432';
const ORANGE_GRADIENT = 'linear-gradient(135deg, #BF3A1A 0%, #E86340 100%)';
const TEAL_DARK = '#0E6059';
const TEAL = '#1C7C73';
const TEAL_LIGHT = '#E8F3F1';
const CARD_BG = '#ECEDE4';
const TEXT = '#1A1A1A';
const TEXT_MUTED = '#8A8F85';
const CARD_SHADOW = '0px 4px 4px 0px rgba(0,0,0,0.25)';

const CARD_STYLE = {
  background: '#FFFFFF',
  borderRadius: '20px',
  boxShadow: CARD_SHADOW,
};

// ─── Constants ────────────────────────────────────────────────────────────────
// "Check for updates" button. Deliberately a labelled pill rather than a bare icon —
// users could not find the icon-only version. Soft teal keeps it clearly secondary to
// the orange "New Ticket" CTA while still being obvious.
const REFRESH_BUTTON_SX = {
  background: TEAL_LIGHT,
  color: TEAL_DARK,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '11.5px', sm: '12.5px' },
  letterSpacing: '0.01em',
  px: { xs: 1.25, sm: 1.8 },
  py: { xs: 0.5, sm: 0.7 },
  minWidth: 0,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  borderRadius: '999px',
  textTransform: 'none',
  border: `1px solid ${TEAL}33`,
  boxShadow: 'none',
  transition: 'background 0.15s, border-color 0.15s, transform 0.15s',
  '& .MuiButton-startIcon': {
    mr: { xs: 0.4, sm: 0.7 },
    ml: 0,
    '& > *:first-of-type': { fontSize: { xs: 15, sm: 17 } },
  },
  // Touch targets need real height on phones even though the pill is compact.
  minHeight: { xs: 32, sm: 34 },
  '&:hover': {
    background: '#D8EBE7',
    borderColor: TEAL,
    transform: 'translateY(-1px)',
  },
  // Keep the pill fully legible while it is working, instead of MUI's grey-out.
  '&.Mui-disabled': { background: TEAL_LIGHT, color: TEAL_DARK, opacity: 0.85 },
};

// "Check for updates" is too wide for a phone header, so collapse to "Refresh" there.
// Two spans toggled by breakpoint rather than useMediaQuery — no JS listener, and no
// flash of the wrong label on first paint.
const RefreshLabel = ({ busy }) => (
  busy ? <>Checking…</> : (
    <>
      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Check for updates</Box>
      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Refresh</Box>
    </>
  )
);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 3;
const ALLOWED_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif', '.webp',
  '.csv', '.pdf', '.xlsx', '.xls', '.json', '.zip', '.txt', '.log',
];

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_on_user: 'Awaiting Reply',
  resolved: 'Resolved',
  closed: 'Closed',
};

// Per-status colours. Every in-flight status previously shared one red palette
// (#FDECEA / #C62828), which read as an error for what are ordinary, healthy states.
// Reuses the same hues as PRIORITY_COLORS below to keep the palette tight.
// Resolved/closed keep the neutral grey they already had.
const STATUS_COLORS = {
  open:            { bg: '#E3F2FD', color: '#1565C0' }, // light blue — newly raised
  in_progress:     { bg: '#E8F5E9', color: '#2E7D32' }, // green — being worked on
  waiting_on_user: { bg: '#FFF3E0', color: '#E65100' }, // amber — needs your reply
  resolved:        { bg: '#F0F0F0', color: TEXT_MUTED },
  closed:          { bg: '#F0F0F0', color: TEXT_MUTED },
};

const PRIORITY_COLORS = {
  low: { bg: '#F5F5F5', color: TEXT_MUTED },
  medium: { bg: '#E3F2FD', color: '#1565C0' },
  high: { bg: '#FFF3E0', color: '#E65100' },
  urgent: { bg: '#FDECEA', color: '#C62828' },
};

const CATEGORY_LABELS = {
  general: 'General',
  billing: 'Billing',
  technical: 'Technical',
  feature_request: 'Feature Request',
};

const KB_LINKS = [
  { label: 'Getting Started Guide' },
  { label: 'API Documentation' },
  { label: 'Integration Tutorials' },
];

// Task 4 — hidden for now: Live Chat, SEO Academy cards.
// Flip these back to true to restore; the JSX below is untouched.
// const SHOW_LIVE_STATUS = false;  // ← Live Status is back, on real data (F2)
// 3.1 — minimums the New Ticket modal enforces. Named so the rule, the helper
// text and the error message can never drift apart.
const MIN_SUBJECT = 5;
const MIN_DESCRIPTION = 20;

const SHOW_LIVE_STATUS = true;
const SHOW_LIVE_CHAT = false;
const SHOW_SEO_ACADEMY = false;

// Live Status presentation, keyed off the measured health state.
const HEALTH_PRESENTATION = {
  checking:    { label: 'CHECKING STATUS…',         dot: '#8A8F85', text: '#3A3F38' },
  operational: { label: 'ALL SYSTEMS OPERATIONAL',  dot: '#2E7D32', text: '#1B5E20' },
  degraded:    { label: 'DEGRADED PERFORMANCE',     dot: '#ED6C02', text: '#7A3E00' },
  down:        { label: 'API UNREACHABLE',          dot: '#C62828', text: '#8E1B1B' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.closed;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1.2,
        py: 0.3,
        borderRadius: '6px',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.6px',
        fontFamily: 'Inter, sans-serif',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
      }}
    >
      {STATUS_LABELS[status] || status}
    </Box>
  );
};

const PriorityBadge = ({ priority }) => {
  const s = PRIORITY_COLORS[priority] || PRIORITY_COLORS.medium;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1.2,
        py: 0.3,
        borderRadius: '6px',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.6px',
        fontFamily: 'Inter, sans-serif',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        ml: 0.8,
      }}
    >
      {priority}
    </Box>
  );
};

const TicketIcon = ({ status }) => {
  const isOpen = ['open', 'in_progress', 'waiting_on_user'].includes(status);
  // Share the badge's palette so the tile and its status label always agree.
  const s = STATUS_COLORS[status] || STATUS_COLORS.closed;
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '14px',
        background: s.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {isOpen ? (
        <ConfirmationNumberIcon sx={{ color: s.color, fontSize: 22 }} />
      ) : (
        <MarkEmailUnreadIcon sx={{ color: s.color, fontSize: 22 }} />
      )}
    </Box>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const SupportTickets = () => {
  const { userData } = useAuth();
  const location = useLocation();
  // Real uptime/latency for the Live Status card. Only polls while the card is
  // actually rendered, so hiding it costs nothing.
  const health = useApiHealth({ enabled: SHOW_LIVE_STATUS });
  // 3.2 — resolved AFTER mount, never during render. If this app is ever
  // server-rendered, deciding platform at render time would emit one string on
  // the server and a different one on the client, which is a hydration
  // mismatch. Starting false means the server-safe (non-Apple) string is what
  // renders first, then it corrects itself on the client.
  const [isApple, setIsApple] = useState(false);
  useEffect(() => { setIsApple(isApplePlatform()); }, []);
  const healthUi = HEALTH_PRESENTATION[health.status] || HEALTH_PRESENTATION.checking;
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [ticketFilter, setTicketFilter] = useState('all');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const dialogFileInputRef = useRef(null);

  const [messageFiles, setMessageFiles] = useState([]);
  const [dialogFiles, setDialogFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [dialogDragOver, setDialogDragOver] = useState(false);

  // History pagination
  const [historyPage, setHistoryPage] = useState(1);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(20);
  const historyRef = useRef(null);

  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
  });

  // 3.1 — per-field validity, plus refs so a failed submit can move focus to
  // the first offending field. Hand-rolled deliberately: this codebase has no
  // form/validation library (no formik, react-hook-form, yup or zod in
  // package.json), and adding one for a two-field modal would be a bigger
  // change than the fix.
  const [fieldErrors, setFieldErrors] = useState({ subject: false, description: false });
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const subjectRef = useRef(null);
  const descriptionRef = useRef(null);

  // Live recovery: once the user has tried to submit, any field that becomes
  // valid drops its red styling immediately — without waiting for another
  // submit attempt. Only ever clears errors here; raising them stays with the
  // submit handler.
  useEffect(() => {
    if (!hasAttemptedSubmit) return;
    const errors = {
      subject: newTicket.subject.trim().length < MIN_SUBJECT,
      description: newTicket.description.trim().length < MIN_DESCRIPTION,
    };
    setFieldErrors((prev) => (
      prev.subject === errors.subject && prev.description === errors.description ? prev : errors
    ));
  }, [newTicket.subject, newTicket.description, hasAttemptedSubmit]);

  useEffect(() => {
    if (userData?.id) loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticketDetail?.messages]);

  // Background list refresh (every 15s) so a status change shows up in "Your
  // Tickets" / "Support History" without the user reloading the whole page.
  // `silent` skips the loading spinner so this doesn't flicker the table.
  useEffect(() => {
    if (!userData?.id) return;
    const interval = setInterval(() => loadTickets(true), 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  // Background detail refresh (every 4s) while a ticket is open — this is
  // what actually fixes "status only updates after I refresh the ticket",
  // and as a side effect re-pulls messages, which papers over the rare case
  // where a reply doesn't show up until a manual reload (see race guard
  // below for the actual cause of that one).
  useEffect(() => {
    if (!detailOpen || !selectedTicket) return;
    const interval = setInterval(() => loadTicketDetail(selectedTicket, true), 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailOpen, selectedTicket]);

  // Mirrors of the open-ticket state for the realtime handlers below, which are
  // registered once and would otherwise close over the values from first render.
  const detailOpenRef = useRef(detailOpen);
  const selectedTicketRef = useRef(selectedTicket);
  useEffect(() => { detailOpenRef.current = detailOpen; }, [detailOpen]);
  useEffect(() => { selectedTicketRef.current = selectedTicket; }, [selectedTicket]);

  // ── F3: Supabase Realtime — instant updates in both directions ─────────────
  //
  // The polling above already converges (15s list / 4s open ticket), but an
  // admin changing a status or posting a reply should land immediately, and a
  // closed dialog shouldn't wait up to 15s. These subscriptions push the change
  // the moment Postgres commits it, and simply re-read through the API (rather
  // than patching state from the payload) so the row shape stays authoritative.
  //
  // Deliberately additive: the intervals stay as the fallback. If Realtime is
  // not enabled for these tables the channel just never fires and nothing
  // regresses — nothing here throws or blocks.
  //
  // REQUIRES, server-side (one-time Supabase setup):
  //   alter publication supabase_realtime add table support_tickets;
  //   alter publication supabase_realtime add table ticket_messages;
  // plus RLS policies letting the signed-in user read their own rows.
  useEffect(() => {
    if (!userData?.id) return undefined;

    const channel = supabase
      .channel(`support-tickets-user-${userData.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_tickets',
          filter: `user_id=eq.${userData.id}`,
        },
        (payload) => {
          // Status/priority changed, or a new ticket appeared → refresh the
          // list, and the open ticket too if it is the one that changed.
          loadTickets(true);
          const changedId = payload?.new?.id ?? payload?.old?.id;
          if (detailOpenRef.current && selectedTicketRef.current
              && String(changedId) === String(selectedTicketRef.current)) {
            loadTicketDetail(selectedTicketRef.current, true);
          }
        }
      )
      .on(
        'postgres_changes',
        // ticket_messages has no user_id column to filter on, so this listens
        // broadly and discards anything that isn't the open ticket. The guard
        // below is what keeps it cheap.
        { event: 'INSERT', schema: 'public', table: 'ticket_messages' },
        (payload) => {
          const ticketId = payload?.new?.ticket_id;
          if (!detailOpenRef.current || !selectedTicketRef.current) return;
          if (String(ticketId) !== String(selectedTicketRef.current)) return;
          loadTicketDetail(selectedTicketRef.current, true);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
    // Subscribes once per user — the handlers read the open ticket through refs
    // so a ticket being opened/closed never tears down and rebuilds the channel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  // Overview's "Submit a ticket" card deep-links here with ?newTicket=1.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('newTicket') === '1') setNewTicketOpen(true);
  }, [location.search]);

  // ── MERGE (IfrahWorks): manual refresh ──────────────────────────────
  // Complementary to the realtime subscriptions above, not a replacement:
  // realtime and polling are both invisible, so a stale-looking status is
  // indistinguishable from a broken one. This gives the user an explicit way
  // to force a check and SEE that it happened. Kept even with realtime live,
  // since realtime is off until the Supabase publication is enabled.
  // Manual "check for updates". The polls above already keep things current, but they
  // are invisible, so a stale-looking status is indistinguishable from a broken one.
  // This gives the user an explicit way to force a check and see that it happened.
  // Non-silent on the detail so the spinner is visible; silent on the list so the
  // table behind the dialog doesn't flash.
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const handleManualRefresh = async () => {
    if (manualRefreshing) return;
    setManualRefreshing(true);
    try {
      await Promise.all([
        loadTickets(true),
        selectedTicket ? loadTicketDetail(selectedTicket, true) : Promise.resolve(),
      ]);
    } finally {
      setManualRefreshing(false);
    }
  };

  // Every load*() call is tagged with an increasing sequence number, and a
  // response is only applied if it's still the newest in-flight request for
  // that resource. Without this, two overlapping fetches (e.g. the open-ticket
  // fetch and a background poll landing back-to-back) can resolve out of
  // order — an older response arriving last would silently overwrite a newer
  // one, which is exactly the "admin's reply is missing until I refresh again"
  // symptom (the very next refresh just wins the race and looks "fixed").
  const ticketsRequestSeq = useRef(0);
  const ticketDetailRequestSeq = useRef(0);

  const loadTickets = async (silent = false) => {
    if (!userData?.id) return;
    const seq = ++ticketsRequestSeq.current;
    try {
      if (!silent) setLoading(true);
      if (!silent) setError(null);
      const response = await apiService.getUserTickets(userData.id);
      if (seq !== ticketsRequestSeq.current) return; // superseded by a newer request
      if (response.success) setTickets(response.data || []);
      else if (!silent) setError(response.error || 'Failed to load tickets');
    } catch (err) {
      if (seq !== ticketsRequestSeq.current) return;
      console.error('Failed to load tickets:', err);
      if (!silent) setError('Failed to load tickets.');
    } finally {
      if (seq === ticketsRequestSeq.current && !silent) setLoading(false);
    }
  };

  const loadTicketDetail = async (ticketId, silent = false) => {
    const seq = ++ticketDetailRequestSeq.current;
    try {
      if (!silent) setDetailLoading(true);
      if (!silent) setError(null);
      const response = await apiService.getTicketById(ticketId);
      if (seq !== ticketDetailRequestSeq.current) return; // superseded by a newer request
      if (response.success) {
        setTicketDetail(response.data);
        setSelectedTicket(ticketId);
        setDetailOpen(true);
      } else if (!silent) {
        setError(response.error || 'Failed to load ticket details');
      }
    } catch (err) {
      if (seq !== ticketDetailRequestSeq.current) return;
      console.error('Failed to load ticket:', err);
      if (!silent) setError('Failed to load ticket details.');
    } finally {
      if (seq === ticketDetailRequestSeq.current && !silent) setDetailLoading(false);
    }
  };

  // 3.1 — validate on submit, then clear LIVE as the user fixes each field.
  //
  // The old modal disabled the Submit button until everything was valid, so
  // clicking it did literally nothing: no error, no movement, no explanation.
  // The user could not tell whether the app was broken or what it wanted. The
  // button is now always enabled and submitting tells you what is wrong.
  //
  // Errors are only RAISED by a submit attempt (not while first typing, which
  // would shout at someone mid-word), but once raised they clear the instant
  // the field becomes valid — see the effect below.
  const validateNewTicket = () => ({
    subject: newTicket.subject.trim().length < MIN_SUBJECT,
    description: newTicket.description.trim().length < MIN_DESCRIPTION,
  });

  const handleCreateTicket = async () => {
    if (!userData?.id) return;

    const errors = validateNewTicket();
    if (errors.subject || errors.description) {
      setFieldErrors(errors);
      setHasAttemptedSubmit(true);
      // Move focus to the first invalid field so keyboard and screen-reader
      // users are taken to the problem rather than left at the button.
      (errors.subject ? subjectRef : descriptionRef).current?.focus();
      return;
    }
    setFieldErrors({ subject: false, description: false });

    try {
      setSending(true);
      setError(null);
      const system_info = {
        user_email: userData.email || '',
        site_url: userData.site_url || userData.domain || '',
        plugin_version: userData.plugin_version || '',
        wordpress_version: userData.wordpress_version || '',
      };
      const response = await apiService.createSupportTicket({
        user_id: userData.id,
        ...newTicket,
        system_info,
      });
      if (response.success) {
        if (dialogFiles.length > 0 && response.data?.id) {
          try {
            setUploading(true);
            await apiService.uploadTicketFiles(
              response.data.id, dialogFiles, 'user', userData.id.toString()
            );
          } catch (uploadErr) {
            setError('Ticket created but file upload failed.');
          } finally {
            setUploading(false);
          }
        }
        setNewTicketOpen(false);
        setNewTicket({ subject: '', description: '', category: 'general', priority: 'medium' });
        // 3.1 — reset validation with the form, so reopening the modal starts
        // clean rather than showing red fields left over from last time.
        setFieldErrors({ subject: false, description: false });
        setHasAttemptedSubmit(false);
        setDialogFiles([]);
        setSuccessMessage('Ticket created successfully! Our team will respond soon.');
        loadTickets();
        if (response.data?.id) loadTicketDetail(response.data.id);
      } else {
        setError(response.error || 'Failed to create ticket');
      }
    } catch (err) {
      setError('Failed to create ticket. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && messageFiles.length === 0) || !selectedTicket || !userData?.id) return;
    try {
      setSending(true);
      setError(null);
      const messageText = newMessage.trim() || (messageFiles.length > 0 ? '(Attachments)' : '');
      let messageResponse = null;
      if (messageText) {
        messageResponse = await apiService.addTicketMessage(selectedTicket, {
          sender_type: 'user',
          sender_id: userData.id.toString(),
          message: messageText,
        });
      }
      if (messageFiles.length > 0) {
        try {
          setUploading(true);
          await apiService.uploadTicketFiles(
            selectedTicket, messageFiles, 'user', userData.id.toString(),
            messageResponse?.data?.id || null
          );
        } catch (uploadErr) {
          setError('Message sent but file upload failed.');
        } finally {
          setUploading(false);
        }
      }
      if (messageResponse?.success || messageFiles.length > 0) {
        setNewMessage('');
        setMessageFiles([]);
        loadTicketDetail(selectedTicket);
      } else if (messageResponse && !messageResponse.success) {
        setError(messageResponse.error || 'Failed to send message');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // 3.2 — composer key handling.
  //
  // OLD:
  //   const handleKeyPress = (e) => {
  //     if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  //   };
  //
  // Three problems with that, all fixed here:
  //
  // 1. IME COMPOSITION. Someone typing Japanese, Chinese or Korean presses
  //    Enter to COMMIT the candidate they are composing, not to send. The old
  //    handler fired anyway, so those users sent half-finished messages and
  //    could not type at all without the box submitting under them. Guarded via
  //    `isComposing` (plus the legacy keyCode 229, which older browsers use).
  // 2. It was bound to onKeyPress, which React deprecates and which does not
  //    fire consistently for every key. Now onKeyDown.
  // 3. ⌘/Ctrl+Enter did not send — see the note below.
  const handleComposerKeyDown = (e) => {
    // Never intercept a key that is part of an in-progress IME composition.
    if (e.isComposing || e.nativeEvent?.isComposing || e.keyCode === 229) return;

    if (e.key !== 'Enter') return;

    // ⌘+Enter (Apple) / Ctrl+Enter (elsewhere) also sends. This is the shortcut
    // most chat apps train users to expect, and it works from any caret
    // position. Deliberately additive to plain Enter — see the DECISION note in
    // the summary; removing it is a one-line change.
    if (hasSendModifier(e)) {
      e.preventDefault();
      handleSendMessage();
      return;
    }

    // Plain Enter sends; Shift+Enter falls through to insert a newline.
    if (!e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const validateFiles = useCallback((files) => {
    const valid = [];
    for (const file of files) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setError(`File "${file.name}" has unsupported type.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(`File "${file.name}" exceeds 10 MB limit.`);
        continue;
      }
      valid.push(file);
    }
    return valid;
  }, []);

  const addFiles = useCallback((newFiles, target = 'message') => {
    const setter = target === 'dialog' ? setDialogFiles : setMessageFiles;
    const current = target === 'dialog' ? dialogFiles : messageFiles;
    const validated = validateFiles(newFiles);
    const available = MAX_FILES - current.length;
    if (validated.length > available) setError(`Max ${MAX_FILES} files. Only ${available} slot(s) left.`);
    setter(prev => [...prev, ...validated.slice(0, available)]);
  }, [validateFiles, messageFiles, dialogFiles]);

  const removeFile = (index, target = 'message') => {
    const setter = target === 'dialog' ? setDialogFiles : setMessageFiles;
    setter(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = useCallback((e, target = 'message') => {
    e.preventDefault();
    e.stopPropagation();
    if (target === 'dialog') setDialogDragOver(false);
    else setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) addFiles(files, target);
  }, [addFiles]);

  const handleDragOver = useCallback((e, target = 'message') => {
    e.preventDefault();
    e.stopPropagation();
    if (target === 'dialog') setDialogDragOver(true);
    else setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e, target = 'message') => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      if (target === 'dialog') setDialogDragOver(false);
      else setDragOver(false);
    }
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext))
      return <ImageIcon fontSize="small" />;
    return <InsertDriveFileIcon fontSize="small" />;
  };

  const getAttachmentUrl = (storedName) =>
    `${API_BASE_URL}/tickets/attachments/${storedName}`;

  // Counts
  const activeCount = tickets.filter(
    (t) => ['open', 'in_progress', 'waiting_on_user'].includes(t.status)
  ).length;

  const filteredTickets = tickets.filter((t) => {
    if (ticketFilter === 'active')
      return ['open', 'in_progress', 'waiting_on_user'].includes(t.status);
    if (ticketFilter === 'closed')
      return ['resolved', 'closed'].includes(t.status);
    return true;
  });

  const FileChips = ({ files, onRemove }) =>
    files.length > 0 ? (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        {files.map((file, idx) => (
          <Chip
            key={idx}
            icon={getFileIcon(file.name)}
            label={`${file.name} (${formatFileSize(file.size)})`}
            size="small"
            onDelete={() => onRemove(idx)}
            deleteIcon={<DeleteIcon fontSize="small" />}
            sx={{ borderRadius: 1.5, maxWidth: 250 }}
          />
        ))}
      </Stack>
    ) : null;

  return (
    <Box sx={{ pb: 4 }}>
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 4,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 800,
              fontSize: '32px',
              color: TEXT,
            }}
          >
            Support
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              color: TEXT_MUTED,
              fontSize: '14px',
              mt: 0.5,
            }}
          >
            Manage your queries and find quick answers.
          </Typography>
        </Box>
        <Button
          onClick={() => setNewTicketOpen(true)}
          sx={{
            background: ORANGE_GRADIENT,
            color: '#FFFFFF',
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            fontSize: '14px',
            px: 2.5,
            py: 1.2,
            borderRadius: '10px',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(229,84,50,0.30)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': {
              background: 'linear-gradient(135deg, #A83215 0%, #D0572C 100%)',
              boxShadow: '0 6px 16px rgba(229,84,50,0.40)',
            },
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
          New Support Ticket
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* ── Two-column main layout ──────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.8fr',
          gap: 3,
          mb: 3,
          alignItems: 'start',
        }}
      >
        {/* ── Left column ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Knowledge Base Card */}
          <Box sx={{ ...CARD_STYLE, p: '28px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  background: '#FDECEA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MenuBookIcon sx={{ color: ORANGE, fontSize: 22 }} />
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  color: TEXT,
                }}
              >
                Knowledge Base
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                color: TEXT_MUTED,
                lineHeight: 1.65,
                mb: 2.5,
              }}
            >
              Everything you need to know about optimizing your local SEO with ImproveSEO's content engine.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {KB_LINKS.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: '12px 16px',
                    borderRadius: '12px',
                    background: CARD_BG,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { background: '#E0E1D7' },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: TEXT,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <ChevronRightIcon sx={{ color: TEXT_MUTED, fontSize: 18 }} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Live Status Card — REAL data (F2) ────────────────────────────
              Every value is measured by useApiHealth, which polls the admin
              server's /health endpoint every 30s and times the round trip.

              OLD (static placeholders):
                <FiberManualRecordIcon sx={{ color: '#2E7D32' }} />
                ALL SYSTEMS OPERATIONAL
                { label: 'UPTIME',      value: '99.69%' }
                { label: 'API LATENCY', value: '124ms'  }
              ── END OLD ──────────────────────────────────────────────────── */}
          {SHOW_LIVE_STATUS && (
          <Box
            sx={{
              borderRadius: '20px',
              boxShadow: CARD_SHADOW,
              background: 'linear-gradient(135deg, #A8DED8 0%, #85CECC 100%)',
              p: '28px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  color: '#0A3D38',
                }}
              >
                Live Status
              </Typography>
              <Tooltip title={health.lastCheckedAt ? `Last checked ${health.lastCheckedAt.toLocaleTimeString()} — click to re-check` : 'Check now'}>
                <IconButton size="small" onClick={health.refresh} sx={{ color: '#0A3D38', p: '4px' }}>
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
              <FiberManualRecordIcon sx={{ color: healthUi.dot, fontSize: 14 }} />
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '12px',
                  letterSpacing: '0.8px',
                  color: healthUi.text,
                }}
              >
                {healthUi.label}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {[
                {
                  label: 'UPTIME (OBSERVED)',
                  value: health.uptimePct === null ? '—' : `${health.uptimePct.toFixed(health.uptimePct === 100 ? 0 : 2)}%`,
                  // Say plainly what this number is. A historical SLA figure can
                  // only come from a monitor that polls around the clock — point
                  // REACT_APP_STATUS_PAGE_URL at one and the link below appears.
                  hint: `Availability across the ${health.probes} health ${health.probes === 1 ? 'check' : 'checks'} made since you opened this page — not a historical SLA figure.`,
                },
                {
                  label: 'API LATENCY',
                  value: health.latencyMs === null ? '—' : `${health.latencyMs}ms`,
                  hint: 'Round-trip time of the most recent successful health check from this browser.',
                },
              ].map((stat) => (
                <Tooltip key={stat.label} title={stat.hint}>
                  <Box
                    sx={{
                      flex: 1,
                      background: 'rgba(255,255,255,0.35)',
                      borderRadius: '12px',
                      p: '14px 16px',
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.7px',
                        color: '#0A3D38',
                        mb: 0.5,
                      }}
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 800,
                        fontSize: '22px',
                        color: '#0A3D38',
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>

            {health.statusPageUrl && (
              <Typography
                component="a"
                href={health.statusPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-block',
                  mt: 2,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '12px',
                  color: '#0A3D38',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Full status history →
              </Typography>
            )}
          </Box>
          )}
        </Box>

        {/* ── Right column: Your Tickets ── */}
        <Box
          sx={{
            borderRadius: '20px',
            boxShadow: CARD_SHADOW,
            background: CARD_BG,
            overflow: 'hidden',
          }}
        >
          {/* Card header — wraps on narrow screens so the title row, refresh pill and
              filter tabs stack instead of overflowing the card. */}
          <Box
            sx={{
              p: { xs: '18px 18px 0', sm: '24px 28px 0' },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap', minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  color: TEXT,
                }}
              >
                Your Tickets
              </Typography>
              {activeCount > 0 && (
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.3,
                    borderRadius: '20px',
                    background: '#E8F5E9',
                    color: '#2E7D32',
                    fontSize: '12px',
                    fontWeight: 700,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {activeCount} ACTIVE
                </Box>
              )}
              <Button
                onClick={handleManualRefresh}
                disabled={manualRefreshing}
                startIcon={manualRefreshing
                  ? <CircularProgress size={14} sx={{ color: TEAL_DARK }} />
                  : <RefreshIcon sx={{ fontSize: 17 }} />}
                sx={REFRESH_BUTTON_SX}
              >
                <RefreshLabel busy={manualRefreshing} />
              </Button>
            </Box>
            {/* Tabs */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {[
                { key: 'all', label: 'All Tickets' },
                { key: 'active', label: 'Active' },
                { key: 'closed', label: 'Closed' },
              ].map((tab) => (
                <Box
                  key={tab.key}
                  onClick={() => setTicketFilter(tab.key)}
                  sx={{
                    px: 1.6,
                    py: 0.7,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: ticketFilter === tab.key ? 700 : 500,
                    fontSize: '13px',
                    color: ticketFilter === tab.key ? TEXT : TEXT_MUTED,
                    background: ticketFilter === tab.key ? '#FFFFFF' : 'transparent',
                    boxShadow: ticketFilter === tab.key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  {tab.label}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Ticket list */}
          <Box sx={{ p: '16px 20px', display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 200 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: TEAL }} size={28} />
              </Box>
            ) : filteredTickets.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <ConfirmationNumberIcon sx={{ fontSize: 44, color: TEXT_MUTED, mb: 1.5 }} />
                <Typography sx={{ fontFamily: 'Inter, sans-serif', color: TEXT_MUTED, fontSize: '14px' }}>
                  {ticketFilter === 'all' ? 'No tickets yet' : `No ${ticketFilter} tickets`}
                </Typography>
                {ticketFilter === 'all' && (
                  <Button
                    onClick={() => setNewTicketOpen(true)}
                    sx={{
                      mt: 2,
                      background: ORANGE_GRADIENT,
                      color: '#FFFFFF',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2.5,
                      py: 1,
                      borderRadius: '8px',
                      textTransform: 'none',
                    }}
                  >
                    Create your first ticket
                  </Button>
                )}
              </Box>
            ) : (
              filteredTickets.map((ticket) => (
                <Box
                  key={ticket.id}
                  onClick={() => loadTicketDetail(ticket.id)}
                  sx={{
                    background: '#FFFFFF',
                    borderRadius: '16px',
                    p: '16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    cursor: 'pointer',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    transition: 'box-shadow 0.15s, transform 0.1s',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.14)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <TicketIcon status={ticket.status} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '12px',
                          color: TEXT_MUTED,
                        }}
                      >
                        {ticket.ticket_display_id || `#TK-${ticket.id}`}
                      </Typography>
                      <FiberManualRecordIcon sx={{ fontSize: 5, color: TEXT_MUTED }} />
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: TEXT_MUTED }}>
                        {formatDate(ticket.updated_at || ticket.created_at)}
                      </Typography>
                    </Box>
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 700,
                        fontSize: '15px',
                        color: TEXT,
                        mb: 0.5,
                      }}
                    >
                      {ticket.subject}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StatusBadge status={ticket.status} />
                      <PriorityBadge priority={ticket.priority} />
                    </Box>
                  </Box>
                  <ChevronRightIcon sx={{ color: TEXT_MUTED, fontSize: 20, flexShrink: 0 }} />
                </Box>
              ))
            )}
          </Box>

          {/* Footer */}
          {tickets.length > 0 && (
            <Box
              sx={{
                px: '28px',
                py: '16px',
                borderTop: '1px solid rgba(0,0,0,0.06)',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                }}
              onClick={() => historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    color: ORANGE,
                  }}
                >
                  View All Support History
                </Typography>
                <OpenInNewIcon sx={{ fontSize: 16, color: ORANGE }} />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Bottom Row: Quick Support cards — Live Chat / SEO Academy hidden,
          see SHOW_LIVE_CHAT / SHOW_SEO_ACADEMY ─────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        {[
          {
            icon: <ForumIcon sx={{ color: '#FFFFFF', fontSize: 24 }} />,
            iconBg: TEAL_DARK,
            title: 'Live Chat',
            subtitle: 'Typical response under 5m',
            show: SHOW_LIVE_CHAT,
          },
          {
            icon: <SchoolIcon sx={{ color: '#FFFFFF', fontSize: 24 }} />,
            iconBg: TEAL,
            title: 'SEO Academy',
            subtitle: 'Master the editorial strategy',
            show: SHOW_SEO_ACADEMY,
          },
        ].filter((card) => card.show).map((card) => (
          <Box
            key={card.title}
            sx={{
              background: TEAL_LIGHT,
              borderRadius: '20px',
              boxShadow: CARD_SHADOW,
              p: '24px 28px',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
              transition: 'box-shadow 0.15s',
              '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.14)' },
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '14px',
                background: card.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {card.icon}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '18px',
                  color: TEXT,
                }}
              >
                {card.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '13px',
                  color: TEXT_MUTED,
                  mt: 0.3,
                }}
              >
                {card.subtitle}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* ── Support History (paginated) ──────────────────────────────────── */}
      {tickets.length > 0 && (
        <Box ref={historyRef} sx={{ mt: 3 }}>
          <Box
            sx={{
              ...CARD_STYLE,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: '24px 28px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '20px',
                    color: TEXT,
                  }}
                >
                  Support History
                </Typography>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.35,
                    borderRadius: '20px',
                    background: CARD_BG,
                    color: TEXT_MUTED,
                    fontSize: '12px',
                    fontWeight: 700,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {tickets.length} TOTAL
                </Box>
              </Box>
              {/* Rows per page */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: TEXT_MUTED }}>
                  Rows per page
                </Typography>
                <FormControl size="small">
                  <Select
                    value={historyRowsPerPage}
                    onChange={(e) => { setHistoryRowsPerPage(Number(e.target.value)); setHistoryPage(1); }}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '13px',
                      fontWeight: 600,
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
                      '& .MuiSelect-select': { py: 0.8, px: 1.5, pr: '32px !important' },
                    }}
                  >
                    {[20, 50, 100].map((n) => (
                      <MenuItem key={n} value={n} sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px' }}>{n}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Table */}
            <TableContainer>
              <Table sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow sx={{ background: CARD_BG }}>
                    {['Ticket ID', 'Subject', 'Category', 'Status', 'Priority', 'Date'].map((col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '11px',
                          letterSpacing: '0.7px',
                          textTransform: 'uppercase',
                          color: TEXT_MUTED,
                          borderBottom: '1px solid rgba(0,0,0,0.06)',
                          py: 1.6,
                          '&:first-of-type': { pl: '28px' },
                          '&:last-of-type': { pr: '28px' },
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets
                    .slice((historyPage - 1) * historyRowsPerPage, historyPage * historyRowsPerPage)
                    .map((ticket, idx) => (
                      <TableRow
                        key={ticket.id}
                        onClick={() => loadTicketDetail(ticket.id)}
                        sx={{
                          cursor: 'pointer',
                          background: idx % 2 === 0 ? '#FFFFFF' : 'rgba(236,237,228,0.35)',
                          transition: 'background 0.12s',
                          '&:hover': { background: '#F5F5F0' },
                          '& .MuiTableCell-root': {
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                            py: 1.6,
                            '&:first-of-type': { pl: '28px' },
                            '&:last-of-type': { pr: '28px' },
                          },
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '13px', color: TEAL }}>
                            {ticket.ticket_display_id || `#TK-${ticket.id}`}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography
                            noWrap
                            sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px', color: TEXT }}
                          >
                            {ticket.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: TEXT_MUTED }}>
                            {CATEGORY_LABELS[ticket.category] || ticket.category}
                          </Typography>
                        </TableCell>
                        <TableCell><StatusBadge status={ticket.status} /></TableCell>
                        <TableCell><PriorityBadge priority={ticket.priority} /></TableCell>
                        <TableCell>
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                            {formatDate(ticket.updated_at || ticket.created_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination footer */}
            {Math.ceil(tickets.length / historyRowsPerPage) > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: '28px',
                  py: '16px',
                  borderTop: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: TEXT_MUTED }}>
                  Showing {(historyPage - 1) * historyRowsPerPage + 1}–{Math.min(historyPage * historyRowsPerPage, tickets.length)} of {tickets.length}
                </Typography>
                <Pagination
                  count={Math.ceil(tickets.length / historyRowsPerPage)}
                  page={historyPage}
                  onChange={(_, val) => {
                    setHistoryPage(val);
                    historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  size="small"
                  shape="rounded"
                  renderItem={(item) => (
                    <PaginationItem
                      {...item}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        borderRadius: '8px',
                        '&.Mui-selected': {
                          background: ORANGE_GRADIENT,
                          color: '#FFFFFF',
                          '&:hover': { background: 'linear-gradient(135deg, #A83215 0%, #D0572C 100%)' },
                        },
                      }}
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ── Ticket Detail Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setTicketDetail(null); setSelectedTicket(null); }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {detailLoading ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress sx={{ color: TEAL }} />
          </Box>
        ) : ticketDetail ? (
          <>
            {/* Dialog header */}
            <Box
              sx={{
                p: { xs: '16px 16px', sm: '20px 24px' },
                borderBottom: '1px solid #F0F0F0',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 0.5, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8, flexWrap: 'wrap' }}>
                  {ticketDetail.ticket.ticket_display_id && (
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12px',
                        color: TEXT_MUTED,
                        fontWeight: 600,
                      }}
                    >
                      {ticketDetail.ticket.ticket_display_id}
                    </Typography>
                  )}
                  <StatusBadge status={ticketDetail.ticket.status} />
                  <PriorityBadge priority={ticketDetail.ticket.priority} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '18px',
                    color: TEXT,
                  }}
                >
                  {ticketDetail.ticket.subject}
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: TEXT_MUTED, mt: 0.4 }}>
                  {CATEGORY_LABELS[ticketDetail.ticket.category]} &bull; Created {formatDate(ticketDetail.ticket.created_at)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Button
                  onClick={handleManualRefresh}
                  disabled={manualRefreshing}
                  startIcon={manualRefreshing
                    ? <CircularProgress size={14} sx={{ color: TEAL_DARK }} />
                    : <RefreshIcon sx={{ fontSize: 17 }} />}
                  sx={REFRESH_BUTTON_SX}
                >
                  <RefreshLabel busy={manualRefreshing} />
                </Button>
                <IconButton
                  onClick={() => { setDetailOpen(false); setTicketDetail(null); setSelectedTicket(null); }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Description */}
            <Box sx={{ px: '24px', py: '16px', borderBottom: '1px solid #F5F5F5' }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: TEXT_MUTED,
                  letterSpacing: '0.8px',
                  textTransform: 'uppercase',
                  mb: 0.8,
                }}
              >
                Description
              </Typography>
              <Typography
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: TEXT, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
              >
                {ticketDetail.ticket.description}
              </Typography>
              {ticketDetail.attachments?.filter((a) => !a.message_id).length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  {ticketDetail.attachments.filter((a) => !a.message_id).map((att) => (
                    <Chip
                      key={att.id}
                      icon={att.mime_type?.startsWith('image/') ? <ImageIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
                      label={att.original_name}
                      size="small"
                      component="a"
                      href={getAttachmentUrl(att.stored_name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      variant="outlined"
                      sx={{ borderRadius: 1.5, maxWidth: 200 }}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: '24px', py: '16px' }}>
              {ticketDetail.messages.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <ForumIcon sx={{ fontSize: 40, color: TEXT_MUTED, mb: 1 }} />
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', color: TEXT_MUTED }}>
                    No messages yet. Our team will respond soon.
                  </Typography>
                </Box>
              ) : (
                ticketDetail.messages.map((msg) => (
                  <Box
                    key={msg.id}
                    sx={{
                      mb: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: msg.sender_type === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 1,
                        flexDirection: msg.sender_type === 'user' ? 'row-reverse' : 'row',
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          mb: 2,
                          bgcolor: msg.sender_type === 'user' ? TEAL : TEAL_LIGHT,
                          color: msg.sender_type === 'user' ? '#FFF' : TEAL_DARK,
                        }}
                      >
                        {msg.sender_type === 'user' ? 'Y' : 'S'}
                      </Avatar>
                      <Box
                        sx={{
                          maxWidth: '72%',
                          p: '12px 16px',
                          borderRadius: '16px',
                          // Your own messages used solid ORANGE with white text, which
                          // read as an error. Use the brand's light teal instead — same
                          // lightness as the support bubble's CARD_BG, so the thread reads
                          // as one calm conversation. Sender is still clear from the
                          // alignment, avatar and "You / Support" caption below.
                          bgcolor: msg.sender_type === 'user' ? TEAL_LIGHT : CARD_BG,
                          color: TEXT,
                          borderBottomRightRadius: msg.sender_type === 'user' ? 4 : undefined,
                          borderBottomLeftRadius: msg.sender_type !== 'user' ? 4 : undefined,
                        }}
                      >
                        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {msg.message}
                        </Typography>
                        {ticketDetail.attachments?.filter((a) => a.message_id === msg.id).length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            {ticketDetail.attachments.filter((a) => a.message_id === msg.id).map((att) => (
                              <Chip
                                key={att.id}
                                icon={att.mime_type?.startsWith('image/') ? <ImageIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
                                label={att.original_name}
                                size="small"
                                component="a"
                                href={getAttachmentUrl(att.stored_name)}
                                target="_blank"
                                rel="noopener noreferrer"
                                clickable
                                sx={{ borderRadius: 1.5, maxWidth: 200 }}
                              />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Box>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: TEXT_MUTED, mt: 0.4, px: 5 }}>
                      {msg.sender_type === 'user' ? 'You' : 'Support'} &bull; {formatDate(msg.created_at)}
                    </Typography>
                  </Box>
                ))
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Reply area */}
            {!['resolved', 'closed'].includes(ticketDetail.ticket.status) ? (
              <Box
                sx={{ p: '16px 24px', borderTop: '1px solid #F0F0F0' }}
                onDrop={(e) => handleDrop(e, 'message')}
                onDragOver={(e) => handleDragOver(e, 'message')}
                onDragLeave={(e) => handleDragLeave(e, 'message')}
              >
                {uploading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
                {dragOver && (
                  <Box sx={{ border: '2px dashed', borderColor: TEAL, borderRadius: '12px', p: 1.5, mb: 1, textAlign: 'center', background: TEAL_LIGHT }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: TEAL }}>
                      Drop files here
                    </Typography>
                  </Box>
                )}
                <FileChips files={messageFiles} onRemove={(i) => removeFile(i, 'message')} />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mt: messageFiles.length > 0 ? 1 : 0 }}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    hidden
                    multiple
                    accept={ALLOWED_EXTENSIONS.join(',')}
                    onChange={(e) => { addFiles(Array.from(e.target.files), 'message'); e.target.value = ''; }}
                  />
                  <Tooltip title="Attach files">
                    <IconButton
                      onClick={() => fileInputRef.current?.click()}
                      disabled={messageFiles.length >= MAX_FILES || sending}
                      size="small"
                      sx={{ border: '1px solid #E0E0E0', borderRadius: '8px' }}
                    >
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* 1.5 — placeholder was "Type your reply...". This composer
                      is used both to add to the original message and to reply,
                      so "reply" was too narrow. Verified repo-wide: it was the
                      only occurrence (no locale files, tests, snapshots or
                      stories reference it). */}
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Type here..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleComposerKeyDown}
                    disabled={sending}
                    size="small"
                    inputProps={{ 'aria-describedby': 'composer-key-hint' }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleSendMessage}
                    disabled={(!newMessage.trim() && messageFiles.length === 0) || sending || uploading}
                    sx={{
                      background: ORANGE,
                      color: '#FFFFFF',
                      borderRadius: '10px',
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      '&:hover': { background: '#BF3A1A' },
                      '&.Mui-disabled': { background: '#E0E0E0', color: TEXT_MUTED },
                    }}
                  >
                    {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
                  </IconButton>
                </Box>
                {/* 3.2 — key names match the user's actual keyboard. "Enter"
                    and "Shift+Enter" are wrong on macOS/iPadOS, where the keys
                    read Return and Shift.
                    OLD: Enter to send &bull; Shift+Enter for new line

                    Resolved after mount (see isApple state) so a server render
                    and the first client render agree — deciding this during
                    render would be a hydration mismatch if this app is ever
                    server-rendered.

                    The glyphs are aria-hidden so a screen reader announces
                    "Return to send", not the raw symbol. */}
                <Typography id="composer-key-hint" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: TEXT_MUTED, mt: 0.8 }}>
                  {isApple ? (
                    <>
                      <span aria-hidden="true">&#9166;&nbsp;</span>Return to send &bull;{' '}
                      <span aria-hidden="true">&#8679;&nbsp;</span>Return for new line
                    </>
                  ) : (
                    <>Enter to send &bull; Shift+Enter for new line</>
                  )}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: '16px 24px', borderTop: '1px solid #F0F0F0', textAlign: 'center', background: CARD_BG }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: TEXT_MUTED }}>
                  This ticket has been {ticketDetail.ticket.status}.
                  {ticketDetail.ticket.status === 'resolved' && ' Create a new ticket if you need further help.'}
                </Typography>
              </Box>
            )}
          </>
        ) : null}
      </Dialog>

      {/* ── New Ticket Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={newTicketOpen}
        onClose={() => !sending && setNewTicketOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px' } }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '20px', color: TEXT }}>
            Create Support Ticket
          </Typography>
          <IconButton size="small" onClick={() => !sending && setNewTicketOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {/* 3.1 — `error` turns the label, outline AND helper text red in one
              go (MUI wires all three), and the helper text always states the
              requirement plus the live count, so colour is never the only
              signal. aria-invalid + aria-describedby make that legible to a
              screen reader; inputRef lets a failed submit move focus here. */}
          <TextField
            fullWidth
            inputRef={subjectRef}
            label="Subject"
            placeholder="Brief summary of your issue"
            value={newTicket.subject}
            onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
            margin="normal"
            required
            error={fieldErrors.subject}
            inputProps={{
              maxLength: 255,
              'aria-invalid': fieldErrors.subject,
              'aria-describedby': 'new-ticket-subject-helper',
            }}
            FormHelperTextProps={{ id: 'new-ticket-subject-helper' }}
            helperText={
              fieldErrors.subject
                ? `Subject must be at least ${MIN_SUBJECT} characters — currently ${newTicket.subject.trim().length}.`
                : `${newTicket.subject.length}/255 characters (min ${MIN_SUBJECT})`
            }
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                value={newTicket.category}
                label="Category"
                onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="billing">Billing</MenuItem>
                <MenuItem value="technical">Technical Issue</MenuItem>
                <MenuItem value="feature_request">Feature Request</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Priority</InputLabel>
              <Select
                value={newTicket.priority}
                label="Priority"
                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField
            fullWidth
            inputRef={descriptionRef}
            label="Description"
            placeholder="Describe your issue in detail. Include error messages, steps to reproduce, etc."
            value={newTicket.description}
            onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
            margin="normal"
            multiline
            rows={5}
            required
            error={fieldErrors.description}
            inputProps={{
              'aria-invalid': fieldErrors.description,
              'aria-describedby': 'new-ticket-description-helper',
            }}
            FormHelperTextProps={{ id: 'new-ticket-description-helper' }}
            helperText={
              fieldErrors.description
                ? `Description must be at least ${MIN_DESCRIPTION} characters — currently ${newTicket.description.trim().length}.`
                : `${newTicket.description.length} characters (min ${MIN_DESCRIPTION})`
            }
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
          <Box sx={{ mt: 1 }}>
            <input
              type="file"
              ref={dialogFileInputRef}
              hidden
              multiple
              accept={ALLOWED_EXTENSIONS.join(',')}
              onChange={(e) => { addFiles(Array.from(e.target.files), 'dialog'); e.target.value = ''; }}
            />
            <Box
              onDrop={(e) => handleDrop(e, 'dialog')}
              onDragOver={(e) => handleDragOver(e, 'dialog')}
              onDragLeave={(e) => handleDragLeave(e, 'dialog')}
              onClick={() => dialogFileInputRef.current?.click()}
              sx={{
                border: '2px dashed',
                borderColor: dialogDragOver ? TEAL : '#E0E0E0',
                borderRadius: '12px',
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                background: dialogDragOver ? TEAL_LIGHT : 'transparent',
                transition: 'all 0.2s',
                '&:hover': { borderColor: TEAL, background: TEAL_LIGHT },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 28, color: dialogDragOver ? TEAL : TEXT_MUTED, mb: 0.5 }} />
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: dialogDragOver ? TEAL : TEXT_MUTED }}>
                Drag & drop files here, or click to browse
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: TEXT_MUTED }}>
                Max {MAX_FILES} files, 10 MB each &bull; Images, PDF, CSV, Excel, Text
              </Typography>
              {dialogFiles.length > 0 && (
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: TEAL, mt: 0.5 }}>
                  {dialogFiles.length}/{MAX_FILES} files attached
                </Typography>
              )}
            </Box>
            <FileChips files={dialogFiles} onRemove={(i) => removeFile(i, 'dialog')} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setNewTicketOpen(false)}
            disabled={sending}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              color: TEXT_MUTED,
            }}
          >
            Cancel
          </Button>
          {/* 3.1 — THE root cause. The button was disabled until every rule
              passed, so "clicking Submit does nothing" was literal: there was
              nothing to click. A disabled control cannot explain itself.
              It is now only disabled while a request is in flight; an invalid
              submit is caught in the handler, which points at the offending
              field and says why.

              OLD:
              disabled={
                !newTicket.subject.trim() ||
                newTicket.subject.length < 5 ||
                !newTicket.description.trim() ||
                newTicket.description.length < 20 ||
                sending
              } */}
          <Button
            onClick={handleCreateTicket}
            disabled={sending}
            sx={{
              background: ORANGE_GRADIENT,
              color: '#FFFFFF',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '14px',
              px: 3,
              py: 1.2,
              borderRadius: '10px',
              textTransform: 'none',
              '&:hover': { background: 'linear-gradient(135deg, #A83215 0%, #D0572C 100%)' },
              '&.Mui-disabled': { background: '#E0E0E0', color: TEXT_MUTED },
            }}
          >
            {sending ? <CircularProgress size={18} color="inherit" /> : 'Submit Ticket'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportTickets;
